// Home › Performance, Blueprint only (BL-123; pages/ux-full screens/dash.js, components/dash.js): one
// page that answers "How is the business doing?" over the dates picked, against the same dates a year
// before: the figures, revenue by month, the phones, then every technician's scorecard. A scorecard
// opens that technician's page, with the jobs behind it. Who sees which part is performance-model.ts's;
// each part is its own action's answer, checked on the server. A technician with no office role sees
// only their own page, as their Home.
import { useState, type CSSProperties } from 'react';
import type { DatePreset } from '@flowology/domain';
import { day, Figures, Label, Meter, Section, type OpenTarget } from '@flowology/ui-web';
import '@flowology/ui-web/base.css';
import { Header, type NavProps } from '../Nav.tsx';
import { todayIn } from '../books/model.ts';
import { useQuery } from '../books/ui.tsx';
import { Loading } from '../shared/Loading.tsx';
import { BOOKS_INCOME, REVENUE_WORDS } from '../shared/revenue-words.ts';
import {
  businessFigs,
  cardsShown,
  cardState,
  chartOf,
  datesInput,
  dollars,
  firstFive,
  monthRows,
  monthWords,
  ownPhoneFigs,
  ownScorecardOnly,
  pct,
  penFor,
  performanceTitle,
  PRESETS,
  rangeLine,
  revenueLine,
  seesBusiness,
  seesPhones,
  seesTechs,
  SORTS,
  stars,
  STATUS_WORDS,
  statusWaits,
  targetWords,
  teamFigs,
  techFigs,
  trendSummary,
  unitsOf,
  type Booking,
  type Business,
  type Card,
  type Detail,
  type Fig,
  type JobRow,
  type JobStatus,
  type MonthPair,
  type RangeOut,
  type Scorecards,
  type SortBy,
  type Targets,
  type Trend,
} from './performance-model.ts';
import './home.css';
import './performance.css';

type Dates = { preset: DatePreset; custom: { from: string; to: string } };

const penStyle = (pen: number | null) =>
  ({ '--pen': pen ? `var(--bp-pen-${pen})` : 'var(--bp-muted)' }) as CSSProperties;

/** A part that's still loading, or that couldn't load, says so in its own place. */
function Part<T>({
  q,
  what,
  children,
}: {
  q: { data: T | null; error: string | null; reload: () => Promise<void> };
  what: string;
  children: (d: T) => React.ReactNode;
}) {
  return q.data == null ? (
    <Loading error={q.error} what={what} retry={() => void q.reload()} />
  ) : (
    children(q.data)
  );
}

// ── the dates ──

function DateBar({
  dates,
  onDates,
  range,
}: {
  dates: Dates;
  onDates: (d: Dates) => void;
  range: RangeOut | null;
}) {
  const [from, setFrom] = useState(dates.custom.from);
  const [to, setTo] = useState(dates.custom.to);
  const pick = (preset: DatePreset) => onDates({ ...dates, preset });
  return (
    <div className="perf-range">
      <div className="seg perf-seg" role="group" aria-label="Dates">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            className="btn quiet"
            aria-pressed={dates.preset === p.id}
            onClick={() => pick(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <label className="perf-rsel">
        <span>Dates</span>
        <select value={dates.preset} onChange={(e) => pick(e.target.value as DatePreset)}>
          {PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>
      {dates.preset === 'custom' && (
        <form
          className="perf-cust"
          onSubmit={(e) => {
            e.preventDefault();
            onDates({ preset: 'custom', custom: { from, to } });
          }}
        >
          <label>
            <span>From</span>
            <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            <span>To</span>
            <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
          </label>
          <button className="btn" type="submit" disabled={!from || !to}>
            Show these dates
          </button>
        </form>
      )}
      {range && <p className="perf-vs">{rangeLine(range)}</p>}
    </div>
  );
}

const figures = (list: Fig[]) => (
  <Figures items={list.map((f) => ({ label: f.label, value: f.value, sub: f.sub }))} />
);

// ── revenue by month ──

function RevenueChart({ months, label }: { months: MonthPair[]; label: string }) {
  const c = chartOf(months);
  return (
    <svg className="perf-chart" viewBox={`0 0 ${c.W} ${c.H}`} role="group" aria-label={label}>
      {c.band && (
        <rect className="perf-c-band" x={c.band.x} y={c.T - 6} width={c.band.w} height={c.base - c.T + 6} />
      )}
      {c.ticks.map((t) => (
        <g key={t.label}>
          <line className="perf-c-grid" x1={c.L} x2={c.W - c.R} y1={t.y} y2={t.y} />
          <text className="perf-c-tick" x={c.L - 8} y={t.y + 4} textAnchor="end">
            {t.label}
          </text>
        </g>
      ))}
      <line className="perf-c-base" x1={c.L} x2={c.W - c.R} y1={c.base} y2={c.base} />
      {c.months.map((g) => {
        const words = monthWords(g.m);
        return (
          <g key={g.m.month} className="perf-c-g" tabIndex={0} role="img" aria-label={words}>
            <title>{words}</title>
            <rect className="perf-c-hit" x={g.gx} y={c.T - 6} width={g.gw} height={c.base - c.T + 6} />
            {g.prev && (
              <rect className="perf-c-prev" x={g.prev.x} y={g.prev.y} width={g.prev.w} height={g.prev.h} />
            )}
            {g.rest && (
              <rect className="perf-c-rest" x={g.rest.x} y={g.rest.y} width={g.rest.w} height={g.rest.h} />
            )}
            {g.cur && (
              <rect className="perf-c-cur" x={g.cur.x} y={g.cur.y} width={g.cur.w} height={g.cur.h} />
            )}
            <text
              className={`perf-c-x${g.m.inRange ? ' perf-c-on' : ''}`}
              x={g.gx + g.gw / 2}
              y={c.base + 16}
              textAnchor="middle"
            >
              {g.m.label}
            </text>
            {g.yearLabel && (
              <text className="perf-c-year" x={g.gx + g.gw / 2} y={c.H - 4} textAnchor="middle">
                {g.yearLabel}
              </text>
            )}
          </g>
        );
      })}
      {c.label && (
        <>
          <text className="perf-c-lab" x={c.W - c.R} y={c.label.y} textAnchor="end">
            {c.label.main}
          </text>
          <text className="perf-c-lab2" x={c.W - c.R} y={c.label.y + 15} textAnchor="end">
            {c.label.sub}
          </text>
        </>
      )}
    </svg>
  );
}

function MonthRows({ months }: { months: MonthPair[] }) {
  return (
    <div className="perf-rows" role="list">
      {monthRows(months).map((r) => (
        <div
          key={r.m.month}
          className={`perf-r${r.m.inRange ? ' perf-r-on' : ''}`}
          role="listitem"
          aria-label={monthWords(r.m)}
        >
          <span className="perf-rm">
            {r.m.label}
            <small>{r.m.year}</small>
          </span>
          <span className="perf-rb" aria-hidden="true">
            <i className="perf-rcur" style={{ width: r.cur }} />
            {r.solid ? (
              <i className="perf-rprev perf-rsplit" style={{ width: r.prev }}>
                <i style={{ width: r.solid }} />
              </i>
            ) : (
              <i className="perf-rprev" style={{ width: r.prev }} />
            )}
          </span>
          <span className="perf-rv">
            {dollars(r.m.revenueCents)}
            {r.m.toDate ? ' so far' : ''}
            <small>
              {dollars(r.m.toDate ? r.m.toDate.yearBeforeCents : r.m.yearBeforeCents)}
              {r.m.toDate ? ` by ${r.m.toDate.through}` : ''}
            </small>
          </span>
        </div>
      ))}
    </div>
  );
}

function Legend({ months, mine }: { months: MonthPair[]; mine?: boolean }) {
  return (
    <p className="perf-leg">
      <span>
        <i className="perf-k perf-k-cur" />
        {mine ? 'Their revenue' : 'Revenue'}
      </span>
      <span>
        <i className="perf-k perf-k-prev" />
        The same month a year before
      </span>
      {months.some((m) => m.toDate) && (
        <span>
          <i className="perf-k perf-k-rest" />
          The rest of that month
        </span>
      )}
      {months.some((m) => m.inRange) && (
        <span>
          <i className="perf-k perf-k-band" />
          Your dates
        </span>
      )}
    </p>
  );
}

function MonthTable({ months }: { months: MonthPair[] }) {
  return (
    <details className="perf-tbl">
      <summary>See the numbers</summary>
      <table className="perf-t">
        <thead>
          <tr>
            <th>Month</th>
            <th className="r">Revenue</th>
            <th className="r">A year before</th>
            <th className="r">Change</th>
          </tr>
        </thead>
        <tbody>
          {[...months].reverse().map((m) => {
            const base = m.toDate ? m.toDate.yearBeforeCents : m.yearBeforeCents;
            const ch = base ? Math.round(((m.revenueCents - base) / base) * 100) : null;
            return (
              <tr key={m.month}>
                <td>
                  {m.label} {m.year}
                  {m.toDate && <small> to {m.toDate.through}</small>}
                </td>
                <td className="r">{dollars(m.revenueCents)}</td>
                <td className="r">
                  {dollars(base)}
                  {m.toDate && <small> ({dollars(m.yearBeforeCents)} the whole month)</small>}
                </td>
                <td className="r">{ch === null ? '—' : `${ch >= 0 ? '+' : '−'}${Math.abs(ch)} %`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </details>
  );
}

function TrendPart({ t, business }: { t: Trend; business: Business | null }) {
  const c = business?.current;
  return (
    <div className="perf-chartbox">
      <p className="perf-sum">{trendSummary(t)}</p>
      {/* what it counts, in the words Reports uses (QA-714) */}
      <p className="flw-sub">
        {REVENUE_WORDS} {BOOKS_INCOME}
      </p>
      <Legend months={t.months} />
      <div className="perf-desk">
        <RevenueChart months={t.months} label={`Revenue by month: ${trendSummary(t)}`} />
      </div>
      <div className="perf-phone">
        <MonthRows months={t.months} />
      </div>
      <MonthTable months={t.months} />
      {c && (
        <dl className="perf-more">
          <div>
            <dt>Opportunities</dt>
            <dd>{c.opportunities.toLocaleString('en-CA')}</dd>
            <small>{c.sold} sold</small>
          </div>
          <div>
            <dt>Memberships sold</dt>
            <dd>{c.membershipsSold}</dd>
            <small>{pct(c.membershipConversion)} of customers without one</small>
          </div>
          <div>
            <dt>Leads set by technicians</dt>
            <dd>{c.leadsSet ?? '—'}</dd>
            <small>
              {c.leadSalesCents === null ? 'Not recorded yet' : `${dollars(c.leadSalesCents)} sold from them`}
            </small>
          </div>
          <div>
            <dt>Customer satisfaction</dt>
            <dd>{stars(c.rating, 2)}</dd>
            <small>
              {c.ratings
                ? `of 5, ${c.ratings} rating${c.ratings === 1 ? '' : 's'}`
                : 'No ratings in these dates'}
            </small>
          </div>
        </dl>
      )}
    </div>
  );
}

// ── the phones ──

function Phones({ b }: { b: Booking }) {
  return (
    <div className="perf-book">
      <dl className="perf-bf">
        <div>
          <dt>Calls</dt>
          <dd>{b.team.calls.toLocaleString('en-CA')}</dd>
        </div>
        <div>
          <dt>Could book</dt>
          <dd>{b.team.leads.toLocaleString('en-CA')}</dd>
        </div>
        <div>
          <dt>Booked</dt>
          <dd>{b.team.booked.toLocaleString('en-CA')}</dd>
        </div>
        <div>
          <dt>Booking rate</dt>
          <dd>{pct(b.team.rate)}</dd>
        </div>
      </dl>
      <p className="perf-note">
        {b.yearBeforeRate === null
          ? 'No calls to compare a year before.'
          : `${pct(b.yearBeforeRate)} the same days a year before.`}{' '}
        Target {pct(b.target)}.
      </p>
      <div className="perf-people">
        {b.rows.map((x) => (
          <div key={x.key ?? x.label} className="perf-pr">
            <span className="perf-pn">
              <b>{x.label}</b>
              {x.you && <span className="perf-you"> You</span>}
              <small>
                {x.booked.toLocaleString('en-CA')} of {x.leads.toLocaleString('en-CA')} booked,{' '}
                {x.calls.toLocaleString('en-CA')} calls
              </small>
            </span>
            <span className="perf-pm">
              {x.rate !== null && (
                <Meter
                  value={Math.round(x.rate * 100)}
                  label={`${x.label}: booking rate ${pct(x.rate)}`}
                  marks={[
                    { at: Math.round(b.target * 100), kind: 'target', label: `Target ${pct(b.target)}` },
                  ]}
                />
              )}
            </span>
            <span className="perf-pv">
              {pct(x.rate)}
              {x.rate !== null && x.rate < b.target && (
                <>
                  <br />
                  <Label kind="wait">under target</Label>
                </>
              )}
            </span>
          </div>
        ))}
        {!b.rows.length && (
          <p className="flw-empty">
            {b.hidden ? 'You took no calls in these dates.' : 'No calls taken in these dates.'}
          </p>
        )}
      </div>
      {b.hidden > 0 && (
        <p className="perf-note">
          The other {b.hidden} {b.hidden === 1 ? 'person’s row is theirs' : 'people’s rows are theirs'} and
          their manager’s to see.
        </p>
      )}
    </div>
  );
}

// ── a technician's scorecard ──

function Who({ c, pen }: { c: Card; pen: number | null }) {
  const state = cardState(c);
  return (
    <span className="perf-who">
      <span className="perf-key" style={penStyle(pen)} aria-hidden="true" />
      <b>{c.name}</b>
      <small>
        {c.businessUnit ?? 'No jobs in these dates'}
        {state ? `, ${state.charAt(0).toLowerCase()}${state.slice(1)}` : ''}
      </small>
    </span>
  );
}

function Cell({
  c,
  pen,
  targets,
  onOpen,
}: {
  c: Card;
  pen: number | null;
  targets: Targets;
  onOpen: () => void;
}) {
  const f = c.current;
  if (!f.jobsCompleted && !f.opportunities && !f.revenueCents)
    return (
      <button type="button" className="perf-cell perf-cell-none" onClick={onOpen}>
        <Who c={c} pen={pen} />
        <span className="perf-none">
          {c.awayUntil ? 'Away: no jobs in these dates' : 'No jobs in these dates'}
        </span>
      </button>
    );
  return (
    <button
      type="button"
      className="perf-cell"
      onClick={onOpen}
      aria-label={`${c.name}: ${dollars(f.revenueCents)} revenue, conversion ${pct(f.conversion)}. Open their jobs`}
    >
      <Who c={c} pen={pen} />
      <span className="perf-rev">
        <b>{dollars(f.revenueCents)}</b>
        <small>{revenueLine(c)}</small>
      </span>
      <span className="perf-conv">
        <span className="perf-cl">Conversion</span>
        <span className="perf-cv">{pct(f.conversion)}</span>
        {f.conversion === null ? (
          <span className="perf-cs">Too few to rate</span>
        ) : (
          <Meter
            value={Math.round(f.conversion * 100)}
            label={`Conversion ${pct(f.conversion)}`}
            marks={[
              {
                at: Math.round(targets.conversion * 100),
                kind: 'target',
                label: `Target ${pct(targets.conversion)}`,
              },
            ]}
          />
        )}
      </span>
      <dl className="perf-mini">
        <div>
          <dt>Opportunities</dt>
          <dd>{f.opportunities}</dd>
        </div>
        <div>
          <dt>Leads set</dt>
          <dd>{f.leadsSet ?? '—'}</dd>
        </div>
        <div>
          <dt>Per opportunity</dt>
          <dd>{f.opportunityAverageCents === null ? '—' : dollars(f.opportunityAverageCents)}</dd>
        </div>
        <div>
          <dt>Lead sales</dt>
          <dd>{f.leadSalesCents === null ? '—' : dollars(f.leadSalesCents)}</dd>
        </div>
        <div>
          <dt>Memberships</dt>
          <dd>{pct(f.membershipConversion)}</dd>
        </div>
        <div>
          <dt>Rating</dt>
          <dd>{stars(f.rating)}</dd>
        </div>
      </dl>
      {f.grossProfitCents !== undefined && (
        <span className="perf-gp">
          Gross profit <b>{dollars(f.grossProfitCents)}</b>,{' '}
          {pct(f.revenueCents ? f.grossProfitCents / f.revenueCents : null)}
        </span>
      )}
    </button>
  );
}

function RateRow({
  label,
  v,
  target,
  sub,
}: {
  label: string;
  v: number | null;
  target: number;
  sub: string;
}) {
  const t = targetWords(v, target);
  return (
    <div className="perf-m">
      <span className="perf-ml">{label}</span>
      <span className="perf-mv">{pct(v)}</span>
      {v !== null && (
        <Meter
          value={Math.round(v * 100)}
          label={`${label} ${pct(v)}`}
          marks={[{ at: Math.round(target * 100), kind: 'target', label: `Target ${pct(target)}` }]}
        />
      )}
      <span className="perf-ms">
        {v === null ? sub || 'Too few to rate' : sub}
        {t && (
          <>
            {sub ? '. ' : ''}
            {t.under ? <Label kind="wait">{t.text}</Label> : t.text}
          </>
        )}
      </span>
    </div>
  );
}

function Row({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="perf-m">
      <span className="perf-ml">{label}</span>
      <span className="perf-mv">{value}</span>
      {sub && <span className="perf-ms">{sub}</span>}
    </div>
  );
}

function FullCard({ c, targets }: { c: Card; targets: Targets }) {
  const f = c.current;
  return (
    <div className="perf-full">
      <Row
        label="Opportunities"
        value={String(f.opportunities)}
        sub={`${f.sold} sold, ${f.jobsCompleted} jobs completed`}
      />
      <RateRow
        label="Conversion rate"
        v={f.conversion}
        target={targets.conversion}
        sub={f.opportunities ? `${f.sold} of ${f.opportunities} opportunities sold` : ''}
      />
      <Row label="Revenue" value={dollars(f.revenueCents)} sub={revenueLine(c)} />
      <Row
        label="Opportunity average"
        value={f.opportunityAverageCents === null ? '—' : dollars(f.opportunityAverageCents)}
        sub="What each opportunity sold, on average"
      />
      <RateRow
        label="Membership conversion"
        v={f.membershipConversion}
        target={targets.memberships}
        sub={
          f.membershipOpportunities
            ? `${f.membershipsSold} of ${f.membershipOpportunities} customers with no membership joined`
            : 'No customers without a membership'
        }
      />
      <Row
        label="Leads set"
        value={f.leadsSet === null ? '—' : String(f.leadsSet)}
        sub="Not recorded yet: the iPad’s “Set a lead” is still to come"
      />
      <Row
        label="Sales from their leads"
        value={f.leadSalesCents === null ? '—' : dollars(f.leadSalesCents)}
        sub="Sold by whoever took the lead"
      />
      <Row
        label="Customer satisfaction"
        value={f.rating === null ? '—' : `${stars(f.rating)} of 5`}
        sub={
          f.ratings ? `From ${f.ratings} rating${f.ratings === 1 ? '' : 's'}` : 'No ratings in these dates'
        }
      />
      {f.grossProfitCents !== undefined && (
        <Row
          label="Gross profit"
          value={dollars(f.grossProfitCents)}
          sub={`${pct(f.revenueCents ? f.grossProfitCents / f.revenueCents : null)} of their revenue. Seen by whoever may see margins`}
        />
      )}
    </div>
  );
}

// ── the page ──

export function PerformanceScreen({
  nav,
  held,
  onOpen,
}: {
  nav: NavProps;
  held: readonly string[];
  /** Opens a job from a technician's list; absent (a technician's own Home), the rows don't open. */
  onOpen?: (t: OpenTarget) => void;
}) {
  const tz = nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = todayIn(tz);
  const [dates, setDates] = useState<Dates>({
    preset: 'month',
    custom: { from: `${today.slice(0, 8)}01`, to: today },
  });
  const [tech, setTech] = useState<string | null>(null);
  const own = ownScorecardOnly(held);
  if (own) {
    return nav.staffId ? (
      <TechPage
        nav={nav}
        staffId={nav.staffId}
        own
        dates={dates}
        onDates={setDates}
        {...(onOpen ? { onOpen } : {})}
      />
    ) : (
      <div className="app bp-home">
        <Header nav={nav} title="Your scorecard" />
        <main className="flw flw-office bp-home-body">
          <p className="flw-empty">Your sign-in has no staff record, so there’s no scorecard to show.</p>
        </main>
      </div>
    );
  }
  if (tech)
    return (
      <TechPage
        nav={nav}
        staffId={tech}
        dates={dates}
        onDates={setDates}
        {...(onOpen ? { onOpen } : {})}
        onBack={() => setTech(null)}
      />
    );
  return <Dashboard nav={nav} held={held} dates={dates} onDates={setDates} onTech={setTech} />;
}

function Dashboard({
  nav,
  held,
  dates,
  onDates,
  onTech,
}: {
  nav: NavProps;
  held: readonly string[];
  dates: Dates;
  onDates: (d: Dates) => void;
  onTech: (id: string) => void;
}) {
  const input = datesInput(dates.preset, dates.custom);
  const business = useQuery<Business>('reports.businessFigures', seesBusiness(held) ? input : null);
  const trend = useQuery<Trend>('reports.revenueTrend', seesBusiness(held) ? input : null);
  const phones = useQuery<Booking>('reports.booking', seesPhones(held) ? input : null);
  const cards = useQuery<Scorecards>('reports.techScorecards', seesTechs(held) ? input : null);
  const [sort, setSort] = useState<SortBy>('revenue');
  const [unit, setUnit] = useState<string | null>(null);
  const range = business.data?.range ?? cards.data?.range ?? phones.data?.range ?? null;
  const figs: Fig[] | null = seesBusiness(held)
    ? business.data
      ? businessFigs(business.data)
      : null
    : seesTechs(held)
      ? cards.data
        ? teamFigs(cards.data.technicians, phones.data)
        : null
      : phones.data
        ? ownPhoneFigs(phones.data)
        : null;
  const figsQ = seesBusiness(held) ? business : seesTechs(held) ? cards : phones;
  const chart = seesBusiness(held) && (
    <Section title="Revenue by month">
      <Part q={trend} what="revenue by month">
        {(t) => <TrendPart t={t} business={business.data} />}
      </Part>
    </Section>
  );
  const allPhones = seesTechs(held);
  const calls = seesPhones(held) && (
    <Section
      title={allPhones ? 'Calls and booking' : 'The team’s calls, and yours'}
      {...(allPhones && phones.data ? { count: phones.data.rows.length } : {})}
    >
      <Part q={phones} what="the calls">
        {(b) => <Phones b={b} />}
      </Part>
    </Section>
  );
  const shown = cards.data ? cardsShown(cards.data.technicians, sort, unit) : [];
  const order = new Map((cards.data?.technicians ?? []).map((c, i) => [c.staffId, penFor(i)]));
  const techs = seesTechs(held) && (
    <Section title="Technicians" {...(cards.data ? { count: shown.length } : {})}>
      <Part q={cards} what="the technicians">
        {(s) => (
          <>
            <div className="perf-tools">
              <div className="seg perf-seg" role="group" aria-label="Sort by">
                {SORTS.map((x) => (
                  <button
                    key={x.id}
                    type="button"
                    className="btn quiet"
                    aria-pressed={sort === x.id}
                    onClick={() => setSort(x.id)}
                  >
                    {x.label}
                  </button>
                ))}
              </div>
              <label className="perf-sel">
                <span>Department</span>
                <select value={unit ?? ''} onChange={(e) => setUnit(e.target.value || null)}>
                  <option value="">All departments</option>
                  {unitsOf(s.technicians).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
              <p className="perf-note">
                Conversion target {pct(s.targets.conversion)}, marked on each bar. Open a technician for their
                jobs.
              </p>
            </div>
            {shown.length ? (
              <div className="perf-cells">
                {shown.map((c) => (
                  <Cell
                    key={c.staffId}
                    c={c}
                    pen={order.get(c.staffId) ?? null}
                    targets={s.targets}
                    onOpen={() => onTech(c.staffId)}
                  />
                ))}
              </div>
            ) : (
              <p className="flw-empty">No technicians in that department.</p>
            )}
          </>
        )}
      </Part>
    </Section>
  );
  return (
    <div className="app bp-home bp-perf">
      <Header nav={nav} title={performanceTitle(held)} lede="Every figure is for the dates below." />
      <main className="flw flw-office bp-home-body">
        <DateBar dates={dates} onDates={onDates} range={range} />
        {figs ? (
          figures(figs)
        ) : (
          <Loading error={figsQ.error} what="the figures" retry={() => void figsQ.reload()} />
        )}
        {chart && calls ? (
          <div className="bp-grid2">
            <div>{chart}</div>
            <div>{calls}</div>
          </div>
        ) : (
          chart
        )}
        {/* the dispatcher's page: the technicians first, then the phones */}
        {techs}
        {!chart && calls}
      </main>
    </div>
  );
}

// ── one technician ──

function TechPage({
  nav,
  staffId,
  own = false,
  dates,
  onDates,
  onOpen,
  onBack,
}: {
  nav: NavProps;
  staffId: string;
  own?: boolean;
  dates: Dates;
  onDates: (d: Dates) => void;
  onOpen?: (t: OpenTarget) => void;
  onBack?: () => void;
}) {
  const [tab, setTab] = useState<'completed' | 'sold'>('completed');
  const [unit, setUnit] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [all, setAll] = useState(false);
  const d = useQuery<Detail>('reports.techDetail', {
    staffId,
    ...datesInput(dates.preset, dates.custom),
    ...(unit ? { businessUnitId: unit } : {}),
    ...(type ? { jobTypeId: type } : {}),
    ...(status ? { status } : {}),
  });
  const card = d.data?.scorecard;
  const title = own ? 'Your scorecard' : (card?.name ?? 'A technician');
  const filtered = !!(unit || type || status);
  const clear = () => {
    setUnit(null);
    setType(null);
    setStatus(null);
    setAll(false);
  };
  return (
    <div className="app bp-home bp-perf">
      <Header
        nav={nav}
        title={title}
        lede={`${own && card ? `${card.name}${card.businessUnit ? `, ${card.businessUnit}` : ''}. ` : ''}${card && cardState(card) ? `${cardState(card)}. ` : ''}Every figure is for the dates below.`}
        {...(onBack ? { crumb: { back: onBack, list: 'Performance' } } : {})}
      >
        {onBack && (
          <button className="btn" onClick={onBack}>
            Back to Performance
          </button>
        )}
        {own && (
          <a className="btn" href="/tech/">
            Open the tech app
          </a>
        )}
      </Header>
      <main className="flw flw-office bp-home-body">
        <DateBar dates={dates} onDates={onDates} range={d.data?.range ?? null} />
        <Part q={d} what={own ? 'your scorecard' : 'their scorecard'}>
          {(x) => (
            <>
              {figures(techFigs(x.scorecard, x.targets))}
              <div className="bp-grid2">
                <div>
                  <Section title={own ? 'Your jobs' : 'Their jobs'}>
                    <div className="perf-tools">
                      <div className="seg perf-seg" role="group" aria-label="Which jobs">
                        {(['completed', 'sold'] as const).map((k) => (
                          <button
                            key={k}
                            type="button"
                            className="btn quiet"
                            aria-pressed={tab === k}
                            onClick={() => {
                              setTab(k);
                              setAll(false);
                            }}
                          >
                            {k === 'completed' ? 'Completed' : 'Sold'} {x[k].total}
                          </button>
                        ))}
                      </div>
                      <label className="perf-sel">
                        <span>Department</span>
                        <select
                          value={unit ?? ''}
                          onChange={(e) => {
                            setUnit(e.target.value || null);
                            setType(null);
                          }}
                        >
                          <option value="">All departments</option>
                          {x.choices.businessUnits.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="perf-sel">
                        <span>Job type</span>
                        <select value={type ?? ''} onChange={(e) => setType(e.target.value || null)}>
                          <option value="">All job types</option>
                          {x.choices.jobTypes.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="perf-sel">
                        <span>Status</span>
                        <select
                          value={status ?? ''}
                          onChange={(e) => setStatus((e.target.value || null) as JobStatus | null)}
                        >
                          <option value="">Any status</option>
                          {x.choices.statuses.map((s) => (
                            <option key={s} value={s}>
                              {STATUS_WORDS[s]}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <p className="perf-note">
                      {x[tab].total} {tab} in {x.range.words}
                      {filtered ? ', filtered' : ''}: {dollars(x[tab].totalCents)}{' '}
                      {filtered && (
                        <button type="button" className="flw-link" onClick={clear}>
                          Clear the filters
                        </button>
                      )}
                    </p>
                    <Jobs
                      rows={x[tab].rows}
                      all={all}
                      onAll={() => setAll(true)}
                      {...(onOpen ? { onOpen } : {})}
                    />
                  </Section>
                </div>
                <div>
                  <Section title="The scorecard">
                    <FullCard c={x.scorecard} targets={x.targets} />
                  </Section>
                  <Section title={own ? 'Your revenue by month' : 'Their revenue by month'}>
                    <Legend months={x.months} mine />
                    <MonthRows months={x.months} />
                  </Section>
                </div>
              </div>
            </>
          )}
        </Part>
      </main>
    </div>
  );
}

function Jobs({
  rows,
  all,
  onAll,
  onOpen,
}: {
  rows: JobRow[];
  all: boolean;
  onAll: () => void;
  onOpen?: (t: OpenTarget) => void;
}) {
  if (!rows.length)
    return (
      <p className="flw-empty">
        <b>No jobs match.</b> Try another department, job type or status, or wider dates.
      </p>
    );
  const shown = firstFive(rows, all);
  return (
    <div className="perf-jobs">
      {shown.rows.map((r) => {
        const body = (
          <>
            <span className="perf-jm">
              <b>{r.number}</b> {r.customerName}
              <small>
                {day(r.date)}. {r.jobType}
                {r.businessUnit ? `, ${r.businessUnit}` : ''}
              </small>
            </span>
            <span className="perf-js">
              <Label kind={statusWaits(r.status) ? 'wait' : 'muted'}>{STATUS_WORDS[r.status]}</Label>
            </span>
            <span className="perf-ja">
              {dollars(r.amountCents)}
              {r.grossProfitCents !== undefined && <small>{dollars(r.grossProfitCents)} gross profit</small>}
            </span>
          </>
        );
        const key = `${r.jobId}-${r.date}`;
        return onOpen ? (
          <button
            key={key}
            type="button"
            className="perf-j"
            onClick={() => onOpen({ kind: 'job', id: r.jobId })}
          >
            {body}
          </button>
        ) : (
          <div key={key} className="perf-j perf-j-still">
            {body}
          </div>
        );
      })}
      {shown.more > 0 && (
        <p className="perf-moreline">
          <button type="button" className="flw-link" onClick={onAll}>
            Show all {rows.length}
          </button>
        </p>
      )}
    </div>
  );
}
